import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, ArrowRight, Check, Star, Quote, FileText, Menu, X,
  Rocket, Award, TrendingUp, Lock, BookOpen, ExternalLink,
} from "lucide-react";
import { Button } from "./ui/button";
import Countdown from "./Countdown";
import PreOrderDialog from "./PreOrderDialog";
import { IMAGES, BOOK, BONUSES, FEATURED, TESTIMONIALS } from "../data/site";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1, ease: "easeOut" } }),
};

export default function LandingPage() {
  const [open, setOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const totalValue = BONUSES.reduce((s, b) => s + b.value, 0);
  const order = () => setOpen(true);

  return (
    <div className="bg-navy text-white overflow-x-hidden">
      <PreOrderDialog open={open} onOpenChange={setOpen} />

      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-navy/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2" data-testid="logo">
            <span className="font-display font-extrabold tracking-tight text-lg">NICHOLAS FORDHAM</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#bonuses" className="hover:text-gold transition-colors">Free Bonuses</a>
            <a href="#book" className="hover:text-gold transition-colors">The Book</a>
            <a href="#author" className="hover:text-gold transition-colors">The Author</a>
            <a href="#proof" className="hover:text-gold transition-colors">Proof</a>
            <a href="#book-two" className="hover:text-gold transition-colors">Book Two</a>
          </nav>
          <Button
            onClick={order}
            data-testid="header-preorder-button"
            className="hidden sm:inline-flex bg-gold hover:bg-gold-hover text-navy font-bold rounded-full px-5 transition-colors"
          >
            Pre-order ${BOOK.price}
          </Button>
          <button className="md:hidden text-white" onClick={() => setNavOpen(!navOpen)} data-testid="mobile-menu-toggle">
            {navOpen ? <X /> : <Menu />}
          </button>
        </div>
        {navOpen && (
          <div className="md:hidden bg-navy border-t border-white/10 px-5 py-4 flex flex-col gap-4 text-white/80">
            <a href="#bonuses" onClick={() => setNavOpen(false)}>Free Bonuses</a>
            <a href="#book" onClick={() => setNavOpen(false)}>The Book</a>
            <a href="#author" onClick={() => setNavOpen(false)}>The Author</a>
            <a href="#proof" onClick={() => setNavOpen(false)}>Proof</a>
            <Button onClick={order} className="bg-gold text-navy font-bold rounded-full">Pre-order ${BOOK.price}</Button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section id="top" className="relative pt-28 sm:pt-36 pb-20 grain">
        <div className="absolute top-0 right-0 w-[55%] h-[80%] bg-gradient-to-bl from-gold/10 to-transparent blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center relative">
          <div>
            <motion.div initial="hidden" animate="show" variants={fadeUp}
              className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              <Sparkles className="h-3.5 w-3.5" /> Pre-sale Now Open
            </motion.div>
            <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp}
              className="font-display font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl xl:text-7xl mt-6">
              Turn Your Passion Into a <span className="gold-text">Profitable Empire</span>
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp}
              className="mt-6 text-lg text-white/70 max-w-xl leading-relaxed">
              <span className="text-white font-semibold">{BOOK.title}</span> — {BOOK.subtitle}. The #1 New York Times
              best-selling roadmap from serial entrepreneur {BOOK.author}. Pre-order today and get 3 priceless
              AI playbooks free.
            </motion.p>

            <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-4">
              <Button onClick={order} data-testid="hero-preorder-button"
                className="h-14 px-8 bg-gold hover:bg-gold-hover text-navy font-bold text-base rounded-full transition-colors group">
                Pre-order for ${BOOK.price}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <div className="text-sm text-white/60">
                <div className="flex items-center gap-1 text-gold">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold" />)}
                </div>
                Loved by 12,000+ entrepreneurs
              </div>
            </motion.div>

            <motion.p initial="hidden" animate="show" custom={3.5} variants={fadeUp}
              className="mt-4 text-sm text-white/55 max-w-xl">
              <Check className="inline h-4 w-4 text-gold mr-1" />
              This is a <span className="text-white/80 font-medium">pre-order</span>: your hardcover ships <span className="text-white/80 font-medium">New Year's Day</span>, and your 3 AI bonus guides (${totalValue} value) are delivered <span className="text-white/80 font-medium">instantly &amp; free</span> after checkout.
            </motion.p>

            <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp}
              className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Ships New Year's Day — countdown</div>
              <Countdown />
            </motion.div>
          </div>

          {/* Book */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }} className="relative flex justify-center">
            <div className="relative animate-float" style={{ perspective: 1000 }}>
              <img src={IMAGES.bookCover} alt="Pursuance of Passion book cover"
                className="book-shadow w-[280px] sm:w-[360px] rounded-md rotate-[4deg] hover:rotate-0 transition-transform duration-500"
                style={{ transform: "rotateY(-12deg) rotateZ(3deg)" }}
                data-testid="hero-book-cover" />
              <div className="absolute -bottom-5 -left-4 sm:-left-10 rounded-2xl bg-white text-navy shadow-2xl px-5 py-4 rotate-[-4deg]">
                <div className="text-2xl font-display font-extrabold">3 FREE</div>
                <div className="text-xs font-semibold text-slate-500">AI Bonus Guides</div>
                <div className="text-xs font-bold text-gold">${totalValue} value</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Value ribbon */}
      <div className="bg-gold text-navy py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center">
              {["Pre-order Bonus", "Business Plans With AI", "Grant Writing With AI", "Securing Funding With AI", "#1 NYT Bestseller", "Signed Edition"].map((t) => (
                <span key={t} className="mx-6 text-sm font-bold uppercase tracking-wider flex items-center gap-3">
                  <Sparkles className="h-4 w-4" /> {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bonuses */}
      <section id="bonuses" className="bg-white text-navy py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-gold">Pre-order exclusive</div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl mt-3">
              3 priceless AI playbooks — <span className="gold-text">yours free</span>
            </h2>
            <p className="text-lg text-slate-600 mt-4">
              Reserve your copy today and we'll instantly hand you the exact AI systems Nicholas uses to build,
              fund, and scale businesses. A combined <span className="font-semibold text-navy">${totalValue} value</span>, free with your ${BOOK.price} pre-order.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {BONUSES.map((b, i) => (
              <motion.div key={b.slug} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                data-testid={`bonus-card-${b.slug}`}
                style={{ borderTop: `4px solid ${b.accent}` }}
                className="group rounded-2xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white mb-5 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: b.accent }}>
                  <FileText className="h-6 w-6" />
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-gold mb-2">Bonus #{i + 1}</div>
                <h3 className="font-display font-bold text-xl leading-snug">{b.title}</h3>
                <p className="text-slate-600 mt-3 text-sm leading-relaxed">{b.desc}</p>
                <div className="mt-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">What's inside</div>
                  <ul className="space-y-2">
                    {b.highlights.map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: b.accent }} />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <span className="text-sm text-slate-400 line-through">${b.value}</span>
                  <span className="text-sm font-bold text-navy bg-gold/20 px-3 py-1 rounded-full">FREE</span>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button onClick={order} data-testid="bonuses-preorder-button"
              className="h-14 px-8 bg-navy hover:bg-navy-light text-white font-bold text-base rounded-full transition-colors">
              Claim all 3 bonuses — Pre-order ${BOOK.price}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Book / what's inside */}
      <section id="book" className="bg-[#F8F9FA] text-navy py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="order-2 lg:order-1">
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-gold">Inside the book</div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">
              The complete success roadmap for the modern entrepreneur
            </h2>
            <p className="text-lg text-slate-600 mt-4">
              Part memoir, part masterclass — {BOOK.author} distills two decades of building multi-million dollar
              companies into a step-by-step system you can run today.
            </p>
            <div className="mt-8 space-y-4">
              {[
                { icon: Rocket, t: "Find & validate your true passion", d: "Stop guessing. Pinpoint the idea worth building your life around." },
                { icon: TrendingUp, t: "Build a scalable business model", d: "Frameworks for products, pricing, and growth that compound." },
                { icon: Award, t: "Fund and scale with confidence", d: "Pitch, raise, and reinvest like a seasoned founder." },
              ].map((f) => (
                <div key={f.t} className="flex gap-4">
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-navy text-gold flex items-center justify-center">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-bold">{f.t}</div>
                    <div className="text-slate-600 text-sm">{f.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2 relative">
            <img src={IMAGES.signing} alt="Nicholas Fordham book signing"
              className="rounded-2xl shadow-xl w-full object-cover h-[420px]" data-testid="book-signing-image" />
          </div>
        </div>
      </section>

      {/* Author */}
      <section id="author" className="bg-navy text-white py-24 grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="h-[480px] overflow-hidden rounded-2xl border border-white/10">
              <img src={IMAGES.jet} alt="Nicholas Fordham"
                className="w-full h-full object-cover object-top" data-testid="author-image" />
            </div>
          </div>
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-gold">The Author</div>
            <h2 className="font-display font-bold tracking-tight text-4xl sm:text-5xl mt-3">Nicholas Fordham</h2>
            <p className="text-lg text-white/70 mt-5 leading-relaxed">
              #1 New York Times best-selling author, serial entrepreneur, and mentor. Nicholas has built multiple
              multi-million dollar businesses from scratch — and now shares the exact roadmap that took him from
              passion to empire.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[["#1", "NYT Bestseller"], ["20+", "Years Building"], ["12k+", "Students Mentored"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-3xl font-extrabold text-gold">{n}</div>
                  <div className="text-sm text-white/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Publishing partnership — Patrick Snow */}
      <section id="coach" className="bg-[#F8F9FA] text-navy py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={IMAGES.patrickSnow} alt="Nicholas Fordham with publishing coach Patrick Snow"
              className="rounded-2xl shadow-xl w-full object-cover h-[460px] object-top"
              data-testid="patrick-snow-image" />
          </div>
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-gold">Publishing Partnership</div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">
              Coached by a master: <span className="gold-text">Patrick Snow</span>
            </h2>
            <p className="text-lg text-slate-600 mt-5 leading-relaxed">
              <span className="font-semibold text-navy">Pursuance of Passion</span> was shaped under the mentorship of
              <span className="font-semibold text-navy"> Patrick Snow</span> — international best-selling author of
              <span className="italic"> "Creating Your Own Destiny: How to Get Exactly What You Want Out of Life"</span> and
              Nicholas Fordham's trusted publishing coach.
            </p>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Patrick has guided hundreds of authors to best-seller status. His proven blueprint is woven through every
              page of this book.
            </p>
            <a href="https://www.patricksnow.com" target="_blank" rel="noopener noreferrer"
              data-testid="patrick-snow-link"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-navy-light text-white font-semibold px-6 h-12 transition-colors">
              Visit PatrickSnow.com
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Proof / featured with */}
      <section id="proof" className="bg-white text-navy py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-sm font-bold uppercase tracking-[0.2em] text-gold">In the room with the best</div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">
              Learning from world-class leaders
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 mt-12">
            {FEATURED.map((f) => (
              <figure key={f.name} className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 shadow-sm"
                data-testid={`featured-${f.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="h-72 overflow-hidden">
                  <img src={f.src} alt={`Nicholas Fordham with ${f.name}`}
                    className="w-full h-full object-cover object-center scale-[1.22]" />
                </div>
                <figcaption className="p-4">
                  <div className="font-bold">{f.name}</div>
                  <div className="text-sm text-slate-500">{f.ctx}</div>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {TESTIMONIALS.map((t, i) => (
              <motion.blockquote key={i} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
                className="rounded-2xl bg-[#F8F9FA] border border-slate-200 p-7">
                <Quote className="h-7 w-7 text-gold" />
                <p className="mt-4 text-slate-700 leading-relaxed">{t.quote}</p>
                <footer className="mt-5">
                  <div className="font-bold text-navy">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </footer>
              </motion.blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Book Two teaser */}
      <section id="book-two" className="bg-[#070d1f] text-white py-24 grain relative overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[60%] h-64 bg-gold/10 blur-3xl rounded-full pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center relative">
          <div className="flex justify-center">
            <div className="relative w-[240px] sm:w-[280px] aspect-[3/4] rounded-md book-shadow rotate-[-4deg] hover:rotate-0 transition-transform duration-500 border border-gold/30 overflow-hidden"
              data-testid="book-two-cover"
              style={{ background: "linear-gradient(160deg,#0A1128 0%,#16203f 100%)" }}>
              <div className="absolute inset-0 grain opacity-40" />
              <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
                <div className="h-12 w-12 rounded-full border border-gold/40 flex items-center justify-center text-gold mb-5">
                  <Lock className="h-5 w-5" />
                </div>
                <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Volume II</div>
                <div className="font-display font-extrabold text-lg leading-tight blur-[3px] select-none">
                  There Is Nothing Artificial With Intelligence
                </div>
                <div className="mt-auto pt-6 text-[11px] uppercase tracking-[0.2em] text-white/40">Cover revealed Jan 1</div>
              </div>
            </div>
          </div>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold">
              <BookOpen className="h-3.5 w-3.5" /> Coming New Year's Day
            </div>
            <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-5 leading-tight">
              The next chapter: <span className="gold-text">Book Two</span>
            </h2>
            <p className="font-display font-extrabold text-xl sm:text-2xl mt-4 leading-snug">
              "There Is Nothing Artificial With Intelligence"
            </p>
            <p className="text-gold/90 text-base mt-1">Discovering The Mindset of the Billionaire</p>
            <p className="text-white/65 mt-5 leading-relaxed">
              Launching alongside your hardcover on <span className="text-white font-medium">New Year's Day</span>.
              Pre-order members get first access and exclusive early-bird pricing before anyone else.
            </p>
            <div className="mt-7">
              <Button onClick={order} data-testid="book-two-preorder-button"
                className="h-12 px-7 bg-white/10 hover:bg-white/20 border border-white/15 text-white font-semibold rounded-full transition-colors">
                Pre-order Book One to get early access
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-navy text-white py-24 relative grain">
        <div className="absolute inset-0 bg-gradient-to-t from-gold/10 to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center relative">
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">
            Pre-order today. Build your empire <span className="gold-text">tomorrow.</span>
          </h2>
          <p className="text-lg text-white/70 mt-5">
            Get your signed copy of {BOOK.title} for ${BOOK.price} and instantly unlock all 3 AI bonus
            guides (${totalValue} value).
          </p>
          <div className="mt-8 flex flex-col items-center gap-6">
            <Button onClick={order} data-testid="final-preorder-button"
              className="h-14 px-10 bg-gold hover:bg-gold-hover text-navy font-bold text-lg rounded-full transition-colors">
              Pre-order for ${BOOK.price}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-4">
              <div className="text-xs uppercase tracking-[0.2em] text-white/50 mb-2">Ships New Year's Day — countdown</div>
              <Countdown />
            </div>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-white/60">
            {["Hardcover ships New Year's Day", "Instant bonus downloads", "Emailed receipt", "Secure Stripe checkout"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><Check className="h-4 w-4 text-gold" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-navy border-t border-white/10 py-10 text-center text-white/50 text-sm">
        <div className="font-display font-extrabold text-white tracking-tight">NICHOLAS FORDHAM</div>
        <p className="mt-2">© {new Date().getFullYear()} Pursuance of Passion. All rights reserved.</p>
        <p className="mt-2">
          Published with coaching from{" "}
          <a href="https://www.patricksnow.com" target="_blank" rel="noopener noreferrer"
            data-testid="footer-patrick-snow-link" className="text-gold hover:underline">Patrick Snow</a>.
        </p>
      </footer>
    </div>
  );
}
